// apps/widget/src/screens/analyzing.screen.tsx
//
// A staged narrative, not a spinner. Analysis takes 10+ seconds, and a spinner
// for that long reads as broken.
//
// The captured photo sits behind a progressive reveal, so there is something to
// watch that is obviously about them.

import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { captureFromUrl } from '../lib/remote-capture';
import { subscribeToScan } from '../lib/sse';
import { t, getLocale } from '../lib/i18n';
import { send } from '../lib/postmessage';
import { useScan } from '../state';

const STAGES = ['analysing.quality', 'analysing.metrics', 'analysing.matching', 'analysing.almost'];

export function AnalyzingScreen() {
  const { state, dispatch } = useScan();
  const [stage, setStage] = useState(0);
  const [stalled, setStalled] = useState(false);

  useEffect(() => {
    if (!state.session) return;

    const key = new URLSearchParams(location.search).get('key') ?? location.pathname.split('/')[2] ?? '';

    const stream = subscribeToScan(state.session.sessionId, key, {
      onEvent: (event) => {
        if (event.thumbnailUrl) {
          dispatch({ type: 'thumbnail', url: event.thumbnailUrl });
          // Taken on a phone: bring the photo here so the scan plays over it.
          if (!state.capture) {
            void captureFromUrl(event.thumbnailUrl).then((capture) => {
              if (capture) dispatch({ type: 'captured', capture });
            });
          }
        }
        // The server's own label wins where it sends one — it knows which stage
        // is actually running.
        // The worker labels its stages in English; anywhere else the widget's
        // own translated stages keep pacing instead.
        if (event.label && getLocale() === 'en') dispatch({ type: 'progress', label: event.label });
      },
      onComplete: (scanId, data) => {
        void (async () => {
          const report = await api.report(scanId);
          dispatch({ type: 'report', report });
          send('gs:scan:complete', {
            scanId,
            skinScore: data['skinScore'],
            skinAge: data['skinAge'],
          });
        })();
      },
      onFailed: (error) => dispatch({ type: 'error', error }),
      onStalled: () => setStalled(true),
    });

    return () => stream.close();
  }, [state.session, dispatch]);

  // Local stage advance is cosmetic pacing between server events, so the copy
  // keeps moving even when the worker is mid-provider-call.
  useEffect(() => {
    const timer = setInterval(() => setStage((s) => Math.min(s + 1, STAGES.length - 1)), 3200);
    return () => clearInterval(timer);
  }, []);

  const stageLabel = state.progressLabel || t(STAGES[stage]!);
  const capture = state.capture;

  // The photo just taken stays where it was, full frame, and the scan plays
  // over it — instead of cutting to a separate screen with a thumbnail, which
  // read as the product having moved on without them.
  if (capture) {
    const { width: w, height: h, landmarks, photoUrl } = capture;
    // A sample of the mesh is enough to read as a scan; all 478 points is a
    // grey smear at this size.
    const dots = landmarks.filter((_, i) => i % 3 === 0);
    const r = Math.max(1.5, w * 0.0028);

    return (
      <div className="gs-capture gs-scanlive">
        <div className="gs-viewport">
          {/* Mirrored like the live preview it replaces, so the frame does not
              flip at the moment of capture. Dots share the SVG, so they stay
              on the face at any panel size. */}
          <svg
            className="gs-scanlive__photo"
            viewBox={`0 0 ${w} ${h}`}
            preserveAspectRatio="xMidYMid slice"
            aria-hidden="true"
          >
            <image href={photoUrl} x={0} y={0} width={w} height={h} />
            <g className="gs-scanlive__mesh">
              {dots.map((p, i) => (
                <circle
                  key={i}
                  cx={p.x * w}
                  cy={p.y * h}
                  r={r}
                  // Revealed top to bottom, in step with the sweep.
                  style={{ animationDelay: `${(p.y * 1.6).toFixed(2)}s` }}
                />
              ))}
            </g>
          </svg>

          <span className="gs-scanning__sweep" aria-hidden="true" />

          <div className="gs-scanlive__status">
            <p className="gs-scanlive__label" role="status" aria-live="polite">
              {stageLabel}
            </p>
            <div className="gs-progress-track" aria-hidden="true">
              <div
                className="gs-progress-fill"
                style={{ width: `${((stage + 1) / STAGES.length) * 100}%` }}
              />
            </div>
            {stalled && (
              <button className="gs-btn gs-btn--ghost" onClick={() => dispatch({ type: 'restart' })}>
                {t('error.stalled')} · {t('error.retry')}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="gs-analysing">
      <div className="gs-reveal">
        {state.thumbnailUrl && <img src={state.thumbnailUrl} alt="" className="gs-reveal-photo" />}
        <div className="gs-reveal-sweep" aria-hidden="true" />
      </div>

      <p className="gs-stage" role="status" aria-live="polite">
        {stageLabel}
      </p>

      <div className="gs-progress-track" aria-hidden="true">
        <div className="gs-progress-fill" style={{ width: `${((stage + 1) / STAGES.length) * 100}%` }} />
      </div>

      {/* Degrades honestly rather than leaving them watching a frozen screen. */}
      {stalled && (
        <div className="gs-stalled">
          <p className="gs-body">{t('error.stalled')}</p>
          <button className="gs-btn gs-btn--ghost" onClick={() => dispatch({ type: 'restart' })}>
            {t('error.retry')}
          </button>
        </div>
      )}
    </div>
  );
}
